import axios from 'axios';
import { API_URL } from '../config';
import { getAppUnlockToken } from './appUnlockStorage';

const ADMIN_PATH_PATTERN = /\/api\/admin\//;

function resolveRequestUrl(config) {
  const raw = config.url || '';
  if (raw.startsWith('http')) return raw;
  const base = (config.baseURL || '').replace(/\/$/, '');
  return base ? `${base}${raw.startsWith('/') ? raw : `/${raw}`}` : raw;
}

function attachUnlockHeader(headers = {}) {
  const unlockToken = getAppUnlockToken();
  if (unlockToken) {
    headers['X-App-Unlock-Token'] = unlockToken;
  }
  return headers;
}

/**
 * Headers for admin API calls (includes app unlock token when present).
 */
export function getAdminApiHeaders(authToken, extra = {}) {
  return attachUnlockHeader({
    Authorization: `Bearer ${authToken}`,
    ...extra,
  });
}

/** Ensure all axios calls to /api/admin/* send the unlock token (mirrors fetch patch in index.js). */
export function setupAdminApiInterceptors() {
  axios.interceptors.request.use((config) => {
    if (!ADMIN_PATH_PATTERN.test(resolveRequestUrl(config))) {
      return config;
    }
    const unlockToken = getAppUnlockToken();
    if (unlockToken) {
      if (!config.headers) config.headers = {};
      config.headers['X-App-Unlock-Token'] = unlockToken;
    }
    return config;
  });
}

export function isAdminLockedError(error) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  return (
    status === 403 &&
    typeof detail === 'string' &&
    detail.toLowerCase().includes('admin area locked')
  );
}

export function getAdminAuthToken() {
  return localStorage.getItem('auth_token');
}

export async function fetchAdminTransactions(token, { limit = 100, search, status, provider, skip = 0 } = {}) {
  const params = { limit, skip };
  if (search) params.search = search;
  if (status) params.status = status;
  if (provider) params.provider = provider;
  const response = await axios.get(`${API_URL}/admin/transactions`, {
    params,
    headers: getAdminApiHeaders(token),
  });
  return response.data;
}

export async function completeAdminTransaction(token, transactionId) {
  const response = await axios.post(
    `${API_URL}/admin/transactions/${transactionId}/complete`,
    {},
    { headers: getAdminApiHeaders(token) }
  );
  return response.data;
}

export async function completeAllPendingAdminTransactions(token) {
  const response = await axios.post(
    `${API_URL}/admin/transactions/complete-all-pending`,
    {},
    { headers: getAdminApiHeaders(token) }
  );
  return response.data;
}

export async function fetchStuckPayments(token, { limit = 100 } = {}) {
  const response = await axios.get(`${API_URL}/admin/stuck-payments`, {
    params: { limit },
    headers: getAdminApiHeaders(token),
  });
  return response.data;
}

export async function retryStuckFulfillment(token, transactionId) {
  const response = await axios.post(
    `${API_URL}/admin/stuck-payments/${transactionId}/retry-fulfillment`,
    {},
    { headers: getAdminApiHeaders(token), timeout: 180000 }
  );
  return response.data;
}

export async function syncStuckTokenFromPaychangu(token, transactionId) {
  const response = await axios.post(
    `${API_URL}/admin/stuck-payments/${transactionId}/sync-token`,
    {},
    { headers: getAdminApiHeaders(token) }
  );
  return response.data;
}

export async function closeStuckAsRefunded(token, transactionId, { note } = {}) {
  const response = await axios.post(
    `${API_URL}/admin/stuck-payments/${transactionId}/close-refunded`,
    note ? { note } : {},
    { headers: getAdminApiHeaders(token) }
  );
  return response.data;
}

export async function fetchSettlementReconciliation(token, params = {}) {
  const response = await axios.get(`${API_URL}/admin/settlements/reconciliation`, {
    params,
    headers: getAdminApiHeaders(token),
  });
  return response.data;
}

export async function syncSettlementCharges(token, body = {}) {
  const response = await axios.post(
    `${API_URL}/admin/settlements/sync-charges`,
    body,
    { headers: getAdminApiHeaders(token), timeout: 120000 }
  );
  return response.data;
}

export async function markSettlements(token, body) {
  const response = await axios.post(
    `${API_URL}/admin/settlements/mark-settled`,
    body,
    { headers: getAdminApiHeaders(token) }
  );
  return response.data;
}

export async function fetchSettlementHistory(token, { limit = 30 } = {}) {
  const response = await axios.get(`${API_URL}/admin/settlements/history`, {
    params: { limit },
    headers: getAdminApiHeaders(token),
  });
  return response.data;
}

export async function settlePaychanguCollection(token) {
  const response = await axios.post(
    `${API_URL}/admin/paychangu/settle-collection`,
    {},
    { headers: getAdminApiHeaders(token), timeout: 120000 }
  );
  return response.data;
}

export async function fetchAdminEarnings(token, { days = 90, limit = 100, skip = 0, status } = {}) {
  const params = { days, limit, skip };
  if (status) params.status = status;
  const response = await axios.get(`${API_URL}/admin/earnings`, {
    params,
    headers: getAdminApiHeaders(token),
  });
  return response.data;
}

export async function fetchAdminUsers(token, { search, limit = 50, skip = 0 } = {}) {
  const params = { limit, skip };
  if (search) params.search = search;
  const response = await axios.get(`${API_URL}/admin/users`, {
    params,
    headers: getAdminApiHeaders(token),
  });
  return response.data;
}

export async function toggleUserAdmin(token, userId) {
  const response = await axios.put(
    `${API_URL}/admin/users/${userId}/toggle-admin`,
    {},
    { headers: getAdminApiHeaders(token) }
  );
  return response.data;
}
